import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { speak } from '../hooks/useSpeech';
import { LogoMark } from '../components/Logo';
import { isWissenEmail } from '../utils/validate';


const BOOT_LINES = [
  'INITIALIZING NEURAL CORE...',
  'LOADING AI ENGINE v2.6...',
  'ESTABLISHING QUANTUM LINK...',
  'VERIFYING CLEARANCE CODES...',
  'SYNCHRONIZING LEADERBOARD...',
  'COMPILING KNOWLEDGE BASE...',
  'NEURALOPS JARVIS ONLINE.',
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(searchParams.get('sso_error') || '');
  const [loading, setLoading] = useState(false);
  const [bootLines, setBootLines] = useState([]);
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    let i = 0;
    const iv = setInterval(() => {
      if (i < BOOT_LINES.length) {
        setBootLines(l => [...l, BOOT_LINES[i]]);
        i++;
      } else {
        clearInterval(iv);
        setTimeout(() => setBooted(true), 400);
      }
    }, 280);
    return () => clearInterval(iv);
  }, []);

  const doLogin = async (e, prefEmail, prefPw) => {
    e?.preventDefault();
    const em = (prefEmail || email).trim();
    if (!isWissenEmail(em)) { setError('Only @wissen.com email addresses are accepted'); return; }
    if (!(prefPw || password)) { setError('Password is required'); return; }
    setLoading(true);
    setError('');
    try {
      const u = await login(prefEmail || email, prefPw || password);
      const hr = new Date().getHours();
      const greeting = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';
      const first = (u?.name || '').split(' ')[0] || 'there';
      // Triggered by a click, so this satisfies browser autoplay policy.
      speak(`${greeting}, ${first}. Welcome back to NeuralOps. JARVIS is online and at your service.`);
      navigate('/app');
    } catch {
      setError('Invalid credentials');
    }
    setLoading(false);
  };

  if (!booted) {
    return (
      <div style={{ height: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 120, height: 120, position: 'relative', marginBottom: 32 }}>
          <div style={{ position: 'absolute', inset: 0, border: '2px solid transparent', borderTopColor: 'var(--j1)', borderRightColor: 'var(--j2)', borderRadius: '50%', animation: 'spin 1.5s linear infinite' }} />
          <div style={{ position: 'absolute', inset: 12, border: '2px solid transparent', borderBottomColor: 'var(--j3)', borderLeftColor: 'var(--j1)', borderRadius: '50%', animation: 'spin 1s linear infinite reverse' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LogoMark size={52} /></div>
        </div>
        <div style={{ fontFamily: 'Share Tech Mono', fontSize: 11, color: 'var(--txt2)', width: 320, height: 140, overflow: 'hidden' }}>
          {bootLines.map((l, i) => (
            <div key={i} style={{ padding: '2px 0', color: i === bootLines.length - 1 ? 'var(--j1)' : 'var(--txt2)', animation: 'fadeIn 0.3s forwards', opacity: 0 }}>
              {`> ${l}`}
            </div>
          ))}
        </div>
        <style>{`@keyframes fadeIn{to{opacity:1}} @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ width: 420, animation: 'slideUp 0.6s ease' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', border: '1px solid var(--brd)', background: 'rgba(0,212,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: -8, borderRadius: '50%', border: '1px solid transparent', borderTopColor: 'var(--j1)', animation: 'spin 4s linear infinite' }} />
            <LogoMark size={44} />
          </div>
          <div style={{ fontFamily: 'Orbitron', fontSize: 24, fontWeight: 700, color: 'var(--txt)', letterSpacing: 4 }}>NEURALOPS</div>
          <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 3, marginTop: 4 }}>AI CENTER OF EXCELLENCE — WISSEN TECHNOLOGY</div>
        </div>

        {/* Login panel */}
        <div className="card" style={{ padding: 28 }}>
          <div style={{ fontFamily: 'Share Tech Mono', fontSize: 10, color: 'var(--txt2)', letterSpacing: 2, textAlign: 'center', marginBottom: 24, textTransform: 'uppercase' }}>Identity Verification Required</div>

          <form onSubmit={doLogin}>
            <div className="form-group">
              <label className="label">Email Address</label>
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@wissen.com" required />
            </div>
            <div className="form-group">
              <label className="label">Password</label>
              <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {error && <div style={{ color: 'var(--j6)', fontSize: 11, fontFamily: 'Share Tech Mono', marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', padding: 13, fontSize: 12, letterSpacing: 3 }}>
              {loading ? 'AUTHENTICATING...' : 'AUTHENTICATE'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--brd2)' }} />
            <span style={{ fontFamily: 'Share Tech Mono', fontSize: 9, color: 'var(--txt3)', letterSpacing: 2 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'var(--brd2)' }} />
          </div>

          {/* Microsoft SSO */}
          <a
            href="/api/auth/sso/authorize"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', padding: '12px 0', borderRadius: 4, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'var(--txt)', textDecoration: 'none',
              fontFamily: 'Share Tech Mono', fontSize: 11, letterSpacing: 2,
              transition: 'border-color .2s, background .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            {/* Microsoft logo */}
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
              <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
              <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
              <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
            </svg>
            SIGN IN WITH MICROSOFT
          </a>
        </div>


        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
          NEURALOPS COE · WISSEN TECHNOLOGY · BENGALURU · INTERNAL CONFIDENTIAL
        </div>
      </div>
    </div>
  );
}
